import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { TURNSTILE_SITE_KEY } from '../config.ts';
import { Button } from './Button.tsx';
import { Icon } from './Icon.tsx';
import { Skeleton } from './Skeleton.tsx';

/* =============================================================================
 * Cloudflare Turnstile bot check.
 *
 * - Loads https://challenges.cloudflare.com/turnstile/v0/api.js once, on demand.
 * - Calls onToken(token) when the check passes, onToken(null) when the token
 *   expires or errors — the submit button should be disabled while it's null.
 * - Tokens are single-use: the parent calls ref.reset() after EVERY failed
 *   submission to get a fresh one (the backend spec requires this).
 * - With VITE_USE_MOCK=true a look-alike demo checkbox is shown instead (the
 *   real script can't load inside the offline demo).
 * ========================================================================== */

export interface TurnstileHandle {
  reset: () => void;
}

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id?: string) => void;
  remove: (id: string) => void;
}
declare global {
  var turnstile: TurnstileApi | undefined;
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptPromise: Promise<TurnstileApi> | null = null;

function loadScript(): Promise<TurnstileApi> {
  if (globalThis.turnstile) return Promise.resolve(globalThis.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = () => (globalThis.turnstile ? resolve(globalThis.turnstile) : reject(new Error('no turnstile')));
      s.onerror = () => {
        scriptPromise = null;
        s.remove();
        reject(new Error('turnstile script failed'));
      };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

type Status = 'loading' | 'ready' | 'verified' | 'error' | 'unconfigured';

export const Turnstile = forwardRef<TurnstileHandle, { onToken: (token: string | null) => void }>(
  function Turnstile({ onToken }, ref) {
    if (import.meta.env.VITE_USE_MOCK === 'true') return <DemoTurnstile ref={ref} onToken={onToken} />;
    return <RealTurnstile ref={ref} onToken={onToken} />;
  },
);

const RealTurnstile = forwardRef<TurnstileHandle, { onToken: (token: string | null) => void }>(
  function RealTurnstile({ onToken }, ref) {
    const box = useRef<HTMLDivElement>(null);
    const widgetId = useRef<string | null>(null);
    const cb = useRef(onToken);
    cb.current = onToken;
    const [status, setStatus] = useState<Status>(TURNSTILE_SITE_KEY ? 'loading' : 'unconfigured');
    const [attempt, setAttempt] = useState(0);

    useImperativeHandle(ref, () => ({
      reset() {
        cb.current(null);
        if (widgetId.current && globalThis.turnstile) {
          globalThis.turnstile.reset(widgetId.current);
          setStatus('ready');
        }
      },
    }));

    useEffect(() => {
      if (!TURNSTILE_SITE_KEY) {
        console.error('VITE_TURNSTILE_SITE_KEY is not set — registration cannot be submitted.');
        return;
      }
      let live = true;
      setStatus('loading');
      loadScript().then(
        (ts) => {
          if (!live || !box.current) return;
          widgetId.current = ts.render(box.current, {
            sitekey: TURNSTILE_SITE_KEY,
            action: 'register',
            theme: 'light',
            size: 'flexible',
            language: 'auto',
            'refresh-expired': 'auto',
            callback: (token: string) => {
              setStatus('verified');
              cb.current(token);
            },
            'expired-callback': () => {
              setStatus('ready');
              cb.current(null);
            },
            'error-callback': () => {
              setStatus('error');
              cb.current(null);
            },
          });
          setStatus('ready');
        },
        () => live && setStatus('error'),
      );
      return () => {
        live = false;
        if (widgetId.current && globalThis.turnstile) globalThis.turnstile.remove(widgetId.current);
        widgetId.current = null;
        cb.current(null);
      };
    }, [attempt]);

    return (
      <div className='bot-check'>
        <p className='bot-check__label'>
          <Icon name='check' size={16} /> सुरक्षा तपासणी
        </p>
        {status === 'loading' && <Skeleton h={65} r={6} />}
        <div ref={box} className='bot-check__widget' hidden={status === 'loading' || status === 'error' || status === 'unconfigured'} />
        {status === 'error' && (
          <div className='bot-check__error'>
            <span>सुरक्षा तपासणी लोड होऊ शकली नाही. इंटरनेट जोडणी तपासून पुन्हा प्रयत्न करा.</span>
            <Button size='sm' variant='secondary' icon='refresh' onClick={() => setAttempt((a) => a + 1)}>पुन्हा लोड करा</Button>
          </div>
        )}
        {status === 'unconfigured' && (
          <div className='bot-check__error'>
            <span>सुरक्षा तपासणी सध्या उपलब्ध नाही. कृपया थोड्या वेळाने प्रयत्न करा किंवा आयोजकांशी संपर्क साधा.</span>
          </div>
        )}
      </div>
    );
  },
);

/** Demo stand-in: looks and behaves like the managed widget (click → short check → token). */
const DemoTurnstile = forwardRef<TurnstileHandle, { onToken: (token: string | null) => void }>(
  function DemoTurnstile({ onToken }, ref) {
    const [state, setState] = useState<'idle' | 'checking' | 'done'>('idle');
    const cb = useRef(onToken);
    cb.current = onToken;
    useEffect(() => () => cb.current(null), []);
    useImperativeHandle(ref, () => ({
      reset() {
        setState('idle');
        onToken(null);
      },
    }));
    const start = () => {
      if (state !== 'idle') return;
      setState('checking');
      setTimeout(() => {
        setState('done');
        onToken(`demo-token-${Date.now()}`);
      }, 900);
    };
    return (
      <div className='bot-check'>
        <p className='bot-check__label'>
          <Icon name='check' size={16} /> सुरक्षा तपासणी
        </p>
        <button type='button' className={`demo-ts demo-ts--${state}`} onClick={start} aria-pressed={state === 'done'}>
          <span className='demo-ts__box'>
            {state === 'checking' ? <span className='spinner' /> : state === 'done' ? <Icon name='check' size={20} /> : null}
          </span>
          <span className='demo-ts__text'>{state === 'done' ? 'यशस्वी!' : state === 'checking' ? 'तपासत आहे…' : 'मी माणूस आहे याची खात्री करा'}</span>
          <span className='demo-ts__brand'>DEMO<br />Turnstile</span>
        </button>
      </div>
    );
  },
);
